import { IBlock } from "../../../framework/src/IBlock";
import { Message } from "../../../framework/src/Message";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { runEngine } from "../../../framework/src/RunEngine";

// Customizable Area Start
import { makeApiMessage } from "../../../components/src/common";

export const configJSON = require("./config");

export interface IScheduleRecipient {
  email: string;
  recipient_type: "to" | "cc";
}

export interface IStoredRecipient {
  id: number;
  email: string;
  recipient_type: "to" | "cc";
}

export interface IScheduleFormData {
  name: string;
  report_type: string;
  rrule: string;
  execution_time: string;
  timezone: string;
  start_date: string;
  end_date: string;
  email_subject: string;
  email_body: string;
  status: string;
  custom_params: string;
  recipients_to: string;
  recipients_cc: string;
}

const reportTypeOptions = [
  { id: "b2b_store_orders_report", name: "B2B Store Orders Report" },
  { id: "b2c_orders_report", name: "B2C Orders Report" },
  { id: "sales_summary_report", name: "Sales Summary Report" },
  { id: "inventory_report", name: "Inventory Report" },
];

const rruleOptions = [
  { id: "FREQ=DAILY", name: "Daily" },
  { id: "FREQ=WEEKLY", name: "Weekly" },
  { id: "FREQ=MONTHLY", name: "Monthly" },
];

const timezoneOptions = [
  { id: "Asia/Riyadh", name: "Asia/Riyadh" },
  { id: "UTC", name: "UTC" },
  { id: "Asia/Dubai", name: "Asia/Dubai" },
];

const statusOptions = [
  { id: "active", name: "Active" },
  { id: "inactive", name: "Inactive" },
];
// Customizable Area End

export interface Props {
  navigation: {
    navigate: Function;
    getParam: Function;
  };
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

export interface S {
  // Customizable Area Start
  formData: IScheduleFormData;
  errors: { [key: string]: string };
  isLoading: boolean;
  isSaving: boolean;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error";
  isEdit: boolean;
  // Customizable Area End
}

interface SS {
  id: any;
  // Customizable Area Start
  // Customizable Area End
}

export default class ReportSchedulerCreateController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  createScheduleApiCallId: string = "";
  getScheduleApiCallId: string = "";
  scheduleId: string | number = "";
  originalRecipients: IStoredRecipient[] = [];
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.receive = this.receive.bind(this);

    // Customizable Area Start
    this.subScribedMessages = [
      getName(MessageEnum.AccoutLoginSuccess),
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.RestAPIResponceSuccessMessage),
      getName(MessageEnum.RestAPIResponceDataMessage),
    ];

    this.state = {
      formData: {
        name: "",
        report_type: "b2b_store_orders_report",
        rrule: "FREQ=DAILY",
        execution_time: "08:00",
        timezone: "Asia/Riyadh",
        start_date: "",
        end_date: "",
        email_subject: "{{report_type}} - {{date}}",
        email_body: "",
        status: "active",
        custom_params: JSON.stringify({
          date_range_type: "relative",
          relative_days: 1,
          region_ids: "all",
          store_ids: "all",
          business_account_id: "",
          filter_columns: ["All"]
        }, null, 2),
        recipients_to: "",
        recipients_cc: "",
      },
      errors: {},
      isLoading: false,
      isSaving: false,
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
      isEdit: false,
    };
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    // Customizable Area End
  }

  async receive(from: string, message: Message) {
    runEngine.debugLog("Message Received", message);

    // Customizable Area Start
    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );

    const successResponse = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (successResponse) {
      if (requestId === this.createScheduleApiCallId) {
        this.handleCreateScheduleResponse(successResponse);
      }
      if (requestId === this.getScheduleApiCallId) {
        this.handleGetScheduleResponse(successResponse);
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  async componentDidMount() {
    super.componentDidMount();
    const scheduleId = this.props.navigation.getParam("id");
    if (scheduleId) {
      this.scheduleId = scheduleId;
      this.setState({ isEdit: true, isLoading: true });
      this.getScheduleDetails();
    } else {
      // Set default start date to today for new schedule
      const today = new Date().toISOString().split("T")[0];
      this.setState({
        formData: {
          ...this.state.formData,
          start_date: today,
        },
      });
    }
  }

  getScheduleDetails = () => {
    const requestMessage = makeApiMessage({
      url: `bx_block_reportscheduler/report_schedules/${this.scheduleId}`,
      method: "GET",
    });

    this.getScheduleApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleGetScheduleResponse = (responseJson: { data?: any }) => {
    this.setState({ isLoading: false });

    if (responseJson && responseJson.data) {
      const data = responseJson.data;

      // Store original recipients with their IDs for edit mode
      // Recipients now come as objects with id and email
      if (data.recipients) {
        this.originalRecipients = [];
        if (data.recipients.to) {
          data.recipients.to.forEach((r: { id: number; email: string }) => {
            this.originalRecipients.push({
              id: r.id,
              email: r.email,
              recipient_type: "to",
            });
          });
        }
        if (data.recipients.cc) {
          data.recipients.cc.forEach((r: { id: number; email: string }) => {
            this.originalRecipients.push({
              id: r.id,
              email: r.email,
              recipient_type: "cc",
            });
          });
        }
      }

      this.setState({
        formData: {
          name: data.name || "",
          report_type: data.report_type || "b2b_store_orders_report",
          rrule: data.rrule || "FREQ=DAILY",
          execution_time: data.execution_time || "08:00",
          timezone: data.timezone || "Asia/Riyadh",
          start_date: data.start_date || "",
          end_date: data.end_date || "",
          email_subject: data.email_subject || "",
          email_body: data.email_body || "",
          status: data.status || "active",
          custom_params: data.custom_params
            ? JSON.stringify(data.custom_params, null, 2)
            : "{}",
          recipients_to: data.recipients?.to?.map((r: { id: number; email: string }) => r.email).join(", ") || "",
          recipients_cc: data.recipients?.cc?.map((r: { id: number; email: string }) => r.email).join(", ") || "",
        },
      });
    }
  }

  handleInputChange = (field: keyof IScheduleFormData, value: string) => {
    this.setState({
      formData: {
        ...this.state.formData,
        [field]: value,
      },
      errors: {
        ...this.state.errors,
        [field]: "",
      },
    });
  };

  validateForm = (): boolean => {
    const { formData } = this.state;
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.report_type) {
      errors.report_type = "Report type is required";
    }

    if (!formData.start_date) {
      errors.start_date = "Start date is required";
    }

    if (!formData.execution_time) {
      errors.execution_time = "Execution time is required";
    }

    if (!formData.recipients_to.trim()) {
      errors.recipients_to = "At least one recipient (To) is required";
    }

    // Validate custom_params is valid JSON
    try {
      JSON.parse(formData.custom_params);
    } catch {
      errors.custom_params = "Custom params must be valid JSON";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = () => {
    if (!this.validateForm()) {
      return;
    }

    this.setState({ isSaving: true });

    const { formData, isEdit } = this.state;

    // Parse current recipients from form
    const currentToEmails = formData.recipients_to
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);

    const currentCcEmails = formData.recipients_cc
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);

    // Build recipients attributes
    let recipientsAttributes: Array<{
      id?: number;
      email?: string;
      recipient_type?: string;
      _destroy?: boolean;
    }> = [];

    if (isEdit) {
      // For edit mode, we need to handle existing recipients properly
      const processedOriginalIds: number[] = [];

      // Process "to" recipients
      currentToEmails.forEach((email) => {
        const existingRecipient = this.originalRecipients.find(
          (r) => r.email === email && r.recipient_type === "to"
        );
        if (existingRecipient) {
          // Existing recipient - include ID to update
          recipientsAttributes.push({
            id: existingRecipient.id,
            email,
            recipient_type: "to",
          });
          processedOriginalIds.push(existingRecipient.id);
        } else {
          // New recipient - no ID
          recipientsAttributes.push({ email, recipient_type: "to" });
        }
      });

      // Process "cc" recipients
      currentCcEmails.forEach((email) => {
        const existingRecipient = this.originalRecipients.find(
          (r) => r.email === email && r.recipient_type === "cc"
        );
        if (existingRecipient) {
          // Existing recipient - include ID to update
          recipientsAttributes.push({
            id: existingRecipient.id,
            email,
            recipient_type: "cc",
          });
          processedOriginalIds.push(existingRecipient.id);
        } else {
          // New recipient - no ID
          recipientsAttributes.push({ email, recipient_type: "cc" });
        }
      });

      // Mark removed recipients for deletion
      this.originalRecipients.forEach((original) => {
        if (!processedOriginalIds.includes(original.id)) {
          recipientsAttributes.push({ id: original.id, _destroy: true });
        }
      });
    } else {
      // For create mode, just add all recipients without IDs
      currentToEmails.forEach((email) => {
        recipientsAttributes.push({ email, recipient_type: "to" });
      });
      currentCcEmails.forEach((email) => {
        recipientsAttributes.push({ email, recipient_type: "cc" });
      });
    }

    // Parse custom_params
    let customParams = {};
    try {
      customParams = JSON.parse(formData.custom_params);
    } catch {
      customParams = {};
    }

    const requestBody = {
      report_schedule: {
        name: formData.name,
        report_type: formData.report_type,
        rrule: formData.rrule,
        execution_time: formData.execution_time,
        timezone: formData.timezone,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        email_subject: formData.email_subject,
        email_body: formData.email_body,
        status: formData.status,
        custom_params: customParams,
        schedule_recipients_attributes: recipientsAttributes,
      },
    };

    const url = isEdit
      ? `bx_block_reportscheduler/report_schedules/${this.scheduleId}`
      : "bx_block_reportscheduler/report_schedules";

    const requestMessage = makeApiMessage({
      url,
      method: isEdit ? "PUT" : "POST",
      body: JSON.stringify(requestBody),
    });

    this.createScheduleApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleCreateScheduleResponse = (responseJson: any) => {
    this.setState({ isSaving: false });
    const { isEdit } = this.state;

    if (responseJson && responseJson.data) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: isEdit
          ? "Schedule updated successfully!"
          : "Schedule created successfully!",
        snackbarSeverity: "success",
      });

      setTimeout(() => {
        this.props.navigation.navigate("ReportScheduler");
      }, 1500);
    } else if (responseJson && responseJson.errors) {
      const errorMessage = Array.isArray(responseJson.errors)
        ? responseJson.errors.join(", ")
        : responseJson.errors;
      this.setState({
        snackbarOpen: true,
        snackbarMessage:
          errorMessage || (isEdit ? "Failed to update schedule" : "Failed to create schedule"),
        snackbarSeverity: "error",
      });
    }
  };

  handleCancel = () => {
    this.props.navigation.navigate("ReportScheduler");
  };

  handleCloseSnackbar = () => {
    this.setState({ snackbarOpen: false });
  };

  getReportTypeOptions = () => reportTypeOptions;
  getRruleOptions = () => rruleOptions;
  getTimezoneOptions = () => timezoneOptions;
  getStatusOptions = () => statusOptions;
  // Customizable Area End
}

export { reportTypeOptions, rruleOptions, timezoneOptions, statusOptions };
